import { createContext, useEffect, PropsWithChildren, useState, useRef, useCallback } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import mixpanel from 'mixpanel-browser';
import { getMixpanelToken, getMode } from '@/config/env';
import { isSyntheticSession } from '@/config/synthetic';

const MIXPANEL_TOKEN = getMixpanelToken();
const MODE = getMode();

// Synthetic monitoring drives the real app every 30 minutes across production,
// stanford, and staging. Those runs land and immediately chat, so counting them
// puts ~144 scripted sessions a day into the same project as real voters and
// pulls any rate measured against landed users toward 100%. Leaving Mixpanel
// uninitialized is what keeps them out: it also suppresses autocaptured
// pageviews, which no per-call guard would catch.
const ANALYTICS_ENABLED = !isSyntheticSession();

if (ANALYTICS_ENABLED) {
  if (!MIXPANEL_TOKEN) {
    throw new Error(
      'Mixpanel token missing. Double check token is in GitHub secrets or .env and retrieve as necessary.'
    );
  }

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: MODE === 'development',
    persistence: 'localStorage',
    autocapture: {
      pageview: 'full-url',
      click: false,
      input: false,
      rage_click: false,
      scroll: false,
      submit: false,
    },
  });
}

export interface AnalyticsContextValue {
  analytics: {
    track: typeof mixpanel.track;
    registerSuperProperties: (props: Record<string, unknown>) => void;
  };
}

export const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

// Unhandled errors are reported as 'Client Error' events, capped per page load
// so an error loop cannot flood the project.
const CLIENT_ERROR_CAP = 25;
const CLIENT_ERROR_MESSAGE_LIMIT = 200;

// Strip the values most likely to carry secrets or PII out of an error string
// before it is sent as an analytics property: URL query strings, email
// addresses, and long token-like blobs.
const scrubErrorMessage = (message: string): string =>
  message
    .replace(/\?[^\s'"]*/g, '?[redacted]')
    .replace(/[\w.+-]+@[\w-]+\.[\w.]+/g, '[email]')
    .replace(/[A-Za-z0-9_-]{24,}/g, '[token]')
    .slice(0, CLIENT_ERROR_MESSAGE_LIMIT);

export const AnalyticsProvider = ({ children }: PropsWithChildren) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(false);
  const sessionStartTime = useRef<number | null>(null);
  const clientErrorCount = useRef(0);

  const track = useCallback((...args: Parameters<typeof mixpanel.track>) => {
    if (!ANALYTICS_ENABLED) return;
    try {
      mixpanel.track(...args);
    } catch (error) {
      // TODO: queue failed events for later retry
      console.warn('mixpanel not ready or error occurred:', error);
    }
  }, []);

  const registerSuperProperties = useCallback((props: Record<string, unknown>) => {
    if (!ANALYTICS_ENABLED) return;
    try {
      mixpanel.register(props);
    } catch (error) {
      console.warn('mixpanel.register failed:', error);
    }
  }, []);

  // Start a new session
  const startSession = useCallback(() => {
    const id = crypto.randomUUID();
    setSessionId(id);
    setIsActive(true);
    sessionStartTime.current = Date.now();
    track('Session Start', { sessionId: id });
  }, [track]);

  // End the current session
  const endSession = useCallback(() => {
    if (sessionId && sessionStartTime.current) {
      track(
        'Session End',
        {
          sessionId,
          duration: Date.now() - sessionStartTime.current,
        },
        { send_immediately: true }
      );
    }
    setIsActive(false);
    setSessionId(null);
    sessionStartTime.current = null;
  }, [sessionId, track]);

  // Idle detection
  useIdleTimer({
    timeout: 120_000, // 2min
    onIdle: endSession,
    onActive: () => {
      if (!isActive) startSession();
    },
    debounce: 500,
  });

  // Report unhandled errors and promise rejections as 'Client Error' so
  // failures between the user and the API are visible per platform, not just
  // in the browser console.
  useEffect(() => {
    const report = (source: string, raw: unknown) => {
      if (clientErrorCount.current >= CLIENT_ERROR_CAP) return;
      clientErrorCount.current += 1;
      const message = raw instanceof Error ? raw.message : String(raw ?? 'Unknown error');
      track('Client Error', {
        source,
        error_message: scrubErrorMessage(message),
      });
    };
    // Prefer the Error object over event.message so instanceof handling (and
    // any future stack capture) works; cross-origin scripts may only set message.
    const handleError = (event: ErrorEvent) => report('window_error', event.error ?? event.message);
    const handleRejection = (event: PromiseRejectionEvent) =>
      report('unhandled_rejection', event.reason);
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [track]);

  // End session when user leaves
  useEffect(() => {
    const handleUnload = () => endSession();
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [endSession, sessionId]);

  // Start session on mount
  useEffect(() => {
    startSession();
  }, [startSession]);

  const value = {
    analytics: { track, registerSuperProperties },
  };

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};
